import React, { memo, useCallback } from 'react';
import { toBlob, toPng } from 'html-to-image';

import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import SendScreenshot from 'components/Dialogs/SendScreenshot';

type ScreenshotButtonProps = {
  containerToPrint: React.RefObject<any>;
};

const options = [
  'Tirar print e fazer download',
  'Tirar print e enviar por e-mail',
];

function ScreenshotButton({
  containerToPrint,
}: ScreenshotButtonProps) {
  const [open, setOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [screenshotUrl, setScreenshotBlob] = React.useState<Blob | null>(null);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handlePrintToBlob = useCallback(async () => {
    if (containerToPrint.current === null) {
      return;
    }

    const url = await toBlob(containerToPrint.current, { cacheBust: true });

    setScreenshotBlob(url);
    setIsDialogOpen(true);
  }, [containerToPrint]);

  const handlePrintDownload = useCallback(() => {
    if (containerToPrint.current === null) {
      return;
    }

    toPng(containerToPrint.current, { cacheBust: true })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = 'screenshot.png';
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.log(err);
      });
  }, [containerToPrint]);

  const handleClick = () => {
    if (selectedIndex === 0) {
      handlePrintDownload();
    } else {
      handlePrintToBlob();
    }
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleMenu = () => {
    setOpen((prevState) => !prevState);
  };

  return (
    <>
      <ButtonGroup ref={anchorRef} aria-label="tirar print" size="small">
        <Button variant="contained" color="primary" onClick={handleClick}>
          {options[selectedIndex]}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          aria-controls="screenshot-option-menu"
          aria-label="Escolher opção de print"
          aria-haspopup="menu"
          onClick={handleMenu}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        style={{ zIndex: 100 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper style={{ borderRadius: 4 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="screenshot-option-menu">
                  {options.map((option, index) => (
                    <MenuItem
                      key={option}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <SendScreenshot
        open={isDialogOpen}
        dataUrl={screenshotUrl}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
}

export default memo(ScreenshotButton);